import { useEffect } from 'react';
import { useAlertsWS } from '@/hooks/useWS';
import { useAlertsStore } from '@/stores/alertsStore';
import { useAudioStore } from '@/stores/audioStore';
import { eventsAPI } from '@/lib/api';
import { normalizeAlert } from '@/lib/alertNormalizer';
import { playAlertChime } from '@/lib/audio';

// Global alerts subscriber. Mounted once at app shell level so the WS
// connection lives for the entire authenticated session, NOT per-route.
// Previously the WS hook lived only in useDashboardAlerts — meaning the
// /cameras page received zero live updates and tiles couldn't flash on
// new events. Elevating it here means any consumer of `alertsStore`
// (CameraTile, CameraActivityList, EventsTimelineCard) gets the live
// feed regardless of which route is rendered.
//
// One chime per unique event_id: store.push returns 'new' for the OPEN
// frame and 'update' for the per-frame refreshes within the same window.
// We gate the chime on 'new' so each alert window plays at most once.

const startOfTodayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

export const useGlobalAlerts = () => {
  const push = useAlertsStore((s) => s.push);
  const audioEnabled = useAudioStore((s) => s.enabled);

  // One-shot seed at mount — fetches today's alert windows from
  // /events/alerts-list so the store has truthful state immediately
  // (before any live WS frame arrives). Restricted to today only — the
  // Dashboard widgets all show "today on operator's wall clock", pulling
  // yesterday's tail just to filter it on render wastes a payload.
  useEffect(() => {
    let cancelled = false;
    eventsAPI
      .list({ since: startOfTodayISO(), limit: 50 })
      .then((rows) => {
        if (cancelled) return;
        // Seed rows pre-date the user's session — push them but never
        // chime (a row from 2 hours ago shouldn't suddenly ping).
        rows.forEach((r) => push(r));
      })
      .catch(() => {
        /* endpoint may briefly fail (cold cache, expired token mid-refresh);
         * the live WS stream will populate the store as new alerts arrive */
      });
    return () => {
      cancelled = true;
    };
  }, [push]);

  // Live stream. Reconnects with exponential backoff via useAlertsWS.
  useAlertsWS({
    onMessage: (data) => {
      const alert = normalizeAlert(data);
      if (!alert) return;
      const result = push(alert);
      // Chime requires:
      //   1. Truly new alert (not a per-frame refresh of an open window)
      //   2. Global audio toggle ON
      //   3. Camera-level audioFlag ON (undefined = allow, for legacy events)
      //   4. Severity not info (info = test/heartbeat — no chime)
      const cameraAudioOk = alert.audio_alert_enabled !== false;
      if (
        result === 'new' &&
        audioEnabled &&
        cameraAudioOk &&
        alert.severity !== 'info'
      ) {
        playAlertChime();
      }
    },
  });
};
